'use client';

import React, { useState } from 'react';
import {
  ChevronDown, ChevronUp, Zap, AlertOctagon, AlertTriangle,
  CheckCircle2, ArrowRight, TrendingUp, Clock, Star
} from 'lucide-react';
import { Recommendation } from '../lib/auditEngine';

const IMPACT_CONFIG = {
  high: {
    label: 'High Impact',
    color: 'var(--severity-critical)',
    bg: 'var(--severity-critical-bg)',
    border: 'rgba(255,61,87,0.25)',
    icon: AlertOctagon,
    liftRange: '+15–30%',
  },
  medium: {
    label: 'Medium Impact',
    color: 'var(--severity-warning)',
    bg: 'var(--severity-warning-bg)',
    border: 'rgba(255,190,61,0.25)',
    icon: AlertTriangle,
    liftRange: '+5–15%',
  },
  low: {
    label: 'Low Impact',
    color: 'var(--severity-pass)',
    bg: 'var(--severity-pass-bg)',
    border: 'rgba(61,255,143,0.2)',
    icon: CheckCircle2,
    liftRange: '+1–5%',
  },
};

const EFFORT_CONFIG = {
  low: { label: 'Quick win', color: 'var(--severity-pass)', icon: Zap },
  medium: { label: 'Half-day', color: 'var(--severity-warning)', icon: Clock },
  high: { label: 'Multi-day', color: 'var(--severity-critical)', icon: TrendingUp },
};

interface RecommendationCardProps {
  rec: Recommendation;
  index: number;
  isFirst: boolean;
}

function RecommendationCard({ rec, index, isFirst }: RecommendationCardProps) {
  const [expanded, setExpanded] = useState(isFirst);

  const impactConfig = IMPACT_CONFIG[rec.impact];
  const effortConfig = EFFORT_CONFIG[rec.effort];
  const ImpactIcon = impactConfig.icon;
  const EffortIcon = effortConfig.icon;

  return (
    <div
      className="rounded-xl border overflow-hidden transition-all duration-200"
      style={{
        background: 'var(--card)',
        borderColor: isFirst ? impactConfig.border : 'var(--border)',
        boxShadow: isFirst && rec.impact === 'high' ? '0 0 0 1px rgba(255,61,87,0.1), 0 4px 20px rgba(255,61,87,0.08)' : 'none',
      }}
    >
      {/* Card header */}
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center gap-4 p-4 lg:p-5 text-left hover:bg-muted/30 transition-colors duration-150"
        aria-expanded={expanded}
        aria-controls={`rec-body-${rec.id}`}
      >
        {/* Rank number */}
        <div
          className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold font-mono"
          style={{
            background: isFirst ? impactConfig.bg : 'var(--muted)',
            color: isFirst ? impactConfig.color : 'var(--muted-foreground)',
            border: `1px solid ${isFirst ? impactConfig.border : 'var(--border)'}`,
          }}
        >
          {index + 1}
        </div>

        {/* Title + badges */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {isFirst && (
              <span
                className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold"
                style={{ background: impactConfig.bg, color: impactConfig.color }}
              >
                <Star size={9} aria-hidden="true" />
                Fix This First
              </span>
            )}
            <span className="text-sm font-semibold text-foreground truncate">
              {rec.title}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{rec.summary}</p>
        </div>

        {/* Impact + effort badges (desktop) */}
        <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
          <div
            className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium"
            style={{ background: impactConfig.bg, color: impactConfig.color, border: `1px solid ${impactConfig.border}` }}
          >
            <ImpactIcon size={10} aria-hidden="true" />
            {impactConfig.liftRange}
          </div>
          <div
            className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium"
            style={{ background: 'var(--muted)', color: effortConfig.color }}
          >
            <EffortIcon size={10} aria-hidden="true" />
            {effortConfig.label}
          </div>
        </div>

        {/* Expand toggle */}
        <div className="flex-shrink-0 ml-2 text-muted-foreground">
          {expanded ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
        </div>
      </button>

      {/* Expanded body */}
      {expanded && (
        <div
          id={`rec-body-${rec.id}`}
          className="px-4 lg:px-5 pb-5 space-y-4 border-t"
          style={{ borderColor: 'var(--border)' }}
        >
          {/* Mobile badges */}
          <div className="flex items-center gap-2 pt-4 sm:hidden">
            <div
              className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium"
              style={{ background: impactConfig.bg, color: impactConfig.color }}
            >
              <ImpactIcon size={10} aria-hidden="true" />
              {impactConfig.liftRange} lift
            </div>
            <div
              className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium"
              style={{ background: 'var(--muted)', color: effortConfig.color }}
            >
              <EffortIcon size={10} aria-hidden="true" />
              {effortConfig.label}
            </div>
          </div>

          {/* Why it matters */}
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider font-mono mb-2 mt-4">
              Why it matters
            </h4>
            <p className="text-sm text-foreground/80 leading-relaxed">{rec.whyItMatters}</p>
          </div>

          {/* How to fix */}
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider font-mono mb-2">
              How to fix it
            </h4>
            <ul className="space-y-2">
              {rec.steps.map((step, stepIndex) => (
                <li
                  key={`rec-${rec.id}-step-${stepIndex}`}
                  className="flex items-start gap-2.5 text-sm text-foreground/80"
                >
                  <ArrowRight
                    size={14}
                    className="flex-shrink-0 mt-0.5"
                    style={{ color: impactConfig.color }}
                    aria-hidden="true"
                  />
                  <span>{step}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Example / benchmark */}
          {rec.benchmark && (
            <div
              className="flex items-start gap-2.5 p-3 rounded-lg text-xs"
              style={{ background: 'var(--muted)', borderLeft: `2px solid ${impactConfig.color}` }}
            >
              <TrendingUp size={13} style={{ color: impactConfig.color, flexShrink: 0, marginTop: 1 }} aria-hidden="true" />
              <span className="text-muted-foreground">{rec.benchmark}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface RecommendationListProps {
  recommendations: Recommendation[];
}

export default function RecommendationList({ recommendations }: RecommendationListProps) {
  if (recommendations.length === 0) {
    return (
      <div
        className="rounded-2xl border p-12 text-center"
        style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <CheckCircle2 size={40} style={{ color: 'var(--severity-pass)', margin: '0 auto 12px' }} aria-hidden="true" />
        <h3 className="text-lg font-bold text-foreground mb-2">No friction detected</h3>
        <p className="text-sm text-muted-foreground max-w-sm mx-auto">
          All 10 conversion signals passed. Your landing page is well-optimized — run another audit after your next major update.
        </p>
      </div>
    );
  }

  const highCount = recommendations.filter(r => r.impact === 'high').length;
  const mediumCount = recommendations.filter(r => r.impact === 'medium').length;
  const lowCount = recommendations.filter(r => r.impact === 'low').length;

  return (
    <div>
      {/* Section header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-lg font-bold text-foreground">
            {recommendations.length} Actionable Recommendations
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Ranked by conversion impact — fix in order for maximum lift
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs font-mono">
          {highCount > 0 && (
            <span
              className="px-2 py-1 rounded-md"
              style={{ background: 'var(--severity-critical-bg)', color: 'var(--severity-critical)' }}
            >
              {highCount} high
            </span>
          )}
          {mediumCount > 0 && (
            <span
              className="px-2 py-1 rounded-md"
              style={{ background: 'var(--severity-warning-bg)', color: 'var(--severity-warning)' }}
            >
              {mediumCount} medium
            </span>
          )}
          {lowCount > 0 && (
            <span
              className="px-2 py-1 rounded-md"
              style={{ background: 'var(--severity-pass-bg)', color: 'var(--severity-pass)' }}
            >
              {lowCount} low
            </span>
          )}
        </div>
      </div>

      {/* Recommendation cards */}
      <div className="space-y-3">
        {recommendations.map((rec, index) => (
          <RecommendationCard
            key={`recommendation-${rec.id}`}
            rec={rec}
            index={index}
            isFirst={index === 0}
          />
        ))}
      </div>
    </div>
  );
}